"use strict";
/**
 * WebSocket service for real-time project updates
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.webSocketService = exports.WebSocketService = void 0;
exports.useWebSocket = useWebSocket;
const react_1 = __importDefault(require("react"));
class WebSocketService {
    constructor(baseUrl = `ws://localhost:8001/api/v1`) {
        this.baseUrl = baseUrl;
        this.ws = null;
        this.projectId = null;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 1000; // Start with 1 second
        this.listeners = new Map();
    }
    /**
     * Connect to WebSocket for a specific project
     */
    connect(projectId) {
        return new Promise((resolve, reject) => {
            if (this.ws && this.ws.readyState === WebSocket.OPEN && this.projectId === projectId) {
                resolve();
                return;
            }
            this.disconnect();
            this.projectId = projectId;
            const wsUrl = `${this.baseUrl}/ws/projects/${projectId}`;
            console.log(`Connecting to WebSocket: ${wsUrl}`);
            this.ws = new WebSocket(wsUrl);
            this.ws.onopen = () => {
                console.log(`WebSocket connected for project ${projectId}`);
                this.reconnectAttempts = 0;
                this.reconnectDelay = 1000;
                resolve();
            };
            this.ws.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    this.handleMessage(message);
                }
                catch (error) {
                    console.error('Error parsing WebSocket message:', error);
                }
            };
            this.ws.onclose = (event) => {
                console.log(`WebSocket closed for project ${projectId}:`, event.code, event.reason);
                // Auto-reconnect if not manually closed
                if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
                    this.scheduleReconnect();
                }
            };
            this.ws.onerror = (error) => {
                console.error('WebSocket error:', error);
                reject(error);
            };
            // Timeout for connection
            setTimeout(() => {
                if (this.ws?.readyState !== WebSocket.OPEN) {
                    reject(new Error('WebSocket connection timeout'));
                }
            }, 5000);
        });
    }
    /**
     * Disconnect from WebSocket
     */
    disconnect() {
        if (this.ws) {
            this.ws.close(1000, 'Manual disconnect');
            this.ws = null;
        }
        this.projectId = null;
        this.reconnectAttempts = 0;
    }
    /**
     * Add listener for WebSocket messages
     */
    addListener(type, callback) {
        if (!this.listeners.has(type)) {
            this.listeners.set(type, new Set());
        }
        this.listeners.get(type).add(callback);
    }
    /**
     * Remove listener for WebSocket messages
     */
    removeListener(type, callback) {
        const typeListeners = this.listeners.get(type);
        if (typeListeners) {
            typeListeners.delete(callback);
            if (typeListeners.size === 0) {
                this.listeners.delete(type);
            }
        }
    }
    /**
     * Send ping to keep connection alive
     */
    ping() {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ type: 'ping' }));
        }
    }
    /**
     * Get connection status
     */
    isConnected() {
        return this.ws?.readyState === WebSocket.OPEN;
    }
    handleMessage(message) {
        console.log('WebSocket message received:', message);
        // Notify type-specific listeners
        const typeListeners = this.listeners.get(message.type);
        if (typeListeners) {
            typeListeners.forEach(callback => callback(message));
        }
        // Notify all listeners
        const allListeners = this.listeners.get('*');
        if (allListeners) {
            allListeners.forEach(callback => callback(message));
        }
    }
    scheduleReconnect() {
        this.reconnectAttempts++;
        console.log(`Scheduling WebSocket reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${this.reconnectDelay}ms`);
        setTimeout(() => {
            if (this.projectId) {
                this.connect(this.projectId).catch(error => {
                    console.error('WebSocket reconnect failed:', error);
                });
            }
        }, this.reconnectDelay);
        // Exponential backoff
        this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000); // Max 30 seconds
    }
}
exports.WebSocketService = WebSocketService;
// Global WebSocket service instance
exports.webSocketService = new WebSocketService();
// React hook for using WebSocket
function useWebSocket(projectId) {
    const [isConnected, setIsConnected] = react_1.default.useState(false);
    const [lastMessage, setLastMessage] = react_1.default.useState(null);
    react_1.default.useEffect(() => {
        if (!projectId) {
            exports.webSocketService.disconnect();
            setIsConnected(false);
            return;
        }
        const handleMessage = (message) => {
            setLastMessage(message);
        };
        const handleConnection = () => {
            setIsConnected(true);
        };
        const handleDisconnection = () => {
            setIsConnected(false);
        };
        // Add listeners
        exports.webSocketService.addListener('*', handleMessage);
        // Connect
        exports.webSocketService.connect(projectId)
            .then(handleConnection)
            .catch(error => {
            console.error('Failed to connect WebSocket:', error);
            setIsConnected(false);
        });
        // Ping interval to keep connection alive
        const pingInterval = setInterval(() => {
            exports.webSocketService.ping();
        }, 30000); // Every 30 seconds
        return () => {
            clearInterval(pingInterval);
            exports.webSocketService.removeListener('*', handleMessage);
            exports.webSocketService.disconnect();
            setIsConnected(false);
        };
    }, [projectId]);
    return { isConnected, lastMessage };
}
